// apps/web/src/components/AppShell/AppShell.tsx
// WHY: Persistent navigation shell wrapping all authenticated pages.
// Left sidebar with role-gated links organized into Reporting, Modules, and
// System sections. Matches the Figma prototype navigation structure.

import { useLocation, useNavigate } from 'react-router-dom';
import { AppShell as MantineAppShell } from '@mantine/core';
import type { Role } from '@synapse/shared-types';
import { useAuth } from '../../auth/auth-context';
import { useHasPageAccess } from '../../hooks/usePermissions';
import styles from './AppShell.module.css';

// ---------------------------------------------------------------------------
// Navigation link model
// ---------------------------------------------------------------------------

interface NavLink {
  label: string;
  path: string;
  icon: string;
  requiredRoles: Role[];
  section: 'reporting' | 'modules' | 'system';
}

// WHY: Role hierarchy for visibility checks — mirrors backend hasAnyRole logic.
function hasAccess(userRoles: Role[], requiredRoles: Role[]): boolean {
  if (userRoles.includes('owner')) return true;
  for (const required of requiredRoles) {
    // WHY: viewer is the lowest role — all authenticated users have viewer access.
    if (required === 'viewer') return true;
    if (userRoles.includes(required)) return true;
    if (required === 'operations' && userRoles.includes('coordinator')) return true;
  }
  return false;
}

// WHY: Navigation links defined statically. Role-based filtering happens at render time.
const NAV_LINKS: NavLink[] = [
  // Reporting
  { label: 'Operations', path: '/operations', icon: '▣', requiredRoles: ['operations'], section: 'reporting' },
  { label: 'Executive', path: '/executive', icon: '◇', requiredRoles: ['executive'], section: 'reporting' },
  { label: 'Board', path: '/board', icon: '◈', requiredRoles: ['board'], section: 'reporting' },
  { label: 'Status', path: '/status', icon: '◉', requiredRoles: ['viewer'], section: 'reporting' },

  // Modules
  { label: 'Integrations', path: '/integrations', icon: '⌖', requiredRoles: ['operations'], section: 'modules' },
  { label: 'Risks', path: '/governance/risks', icon: '⊠', requiredRoles: ['coordinator'], section: 'modules' },
  { label: 'Wins & Asks', path: '/governance/wins-asks', icon: '⌘', requiredRoles: ['coordinator'], section: 'modules' },
  { label: 'Findings', path: '/governance/findings', icon: '⌥', requiredRoles: ['coordinator'], section: 'modules' },
  { label: 'Threat Models', path: '/threat-models', icon: '⛊', requiredRoles: ['operations'], section: 'modules' },
  { label: 'Manual Entry', path: '/coordinator', icon: '✎', requiredRoles: ['coordinator'], section: 'modules' },
  { label: 'Audit Log', path: '/audit', icon: '📋', requiredRoles: ['operations'], section: 'modules' },

  // System
  { label: 'Asset Library', path: '/admin/asset-library', icon: '⊞', requiredRoles: ['coordinator'], section: 'system' },
  { label: 'Admin', path: '/admin', icon: '⚙', requiredRoles: ['owner'], section: 'system' },

];

// ---------------------------------------------------------------------------
// Section labels
// ---------------------------------------------------------------------------

const SECTION_LABELS: Record<NavLink['section'], string> = {
  reporting: 'Reporting',
  modules: 'Modules',
  system: 'System',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const userRoles = user?.roles ?? [];

  // WHY: AI Spend Tracker is dark-by-default and gated on the '/ai-spend'
  // pageAccess PERMISSION (not a role), so it's injected here rather than in
  // the role-filtered NAV_LINKS. Owner holds '*' so it always resolves.
  const canSeeAiSpend = useHasPageAccess('/ai-spend');

  // WHY: Filter links based on user's roles.
  const roleVisibleLinks = NAV_LINKS.filter((link) => hasAccess(userRoles, link.requiredRoles));
  const visibleLinks = canSeeAiSpend
    ? [
        ...roleVisibleLinks,
        { label: 'AI Spend', path: '/ai-spend', icon: '◫', requiredRoles: [], section: 'modules' as const },
      ]
    : roleVisibleLinks;

  // WHY: Group links by section for rendering.
  const sections = (['reporting', 'modules', 'system'] as const).map((section) => ({
    section,
    label: SECTION_LABELS[section],
    links: visibleLinks.filter((l) => l.section === section),
  })).filter((s) => s.links.length > 0);

  // WHY: Determine active link by matching current path.
  // Check exact match first, then prefix match for nested routes.
  const isActive = (path: string) => {
    if (path === '/operations' && location.pathname.startsWith('/operations')) return true;
    if (location.pathname === path) return true;
    return location.pathname.startsWith(path + '/');
  };

  // WHY: Derive display name from ID-token name claims when present.
  // Falls back to the email local-part, then "User", so the badge always
  // shows something meaningful even if the IdP doesn't supply name claims.
  const fullName =
    user?.givenName && user.familyName
      ? `${user.givenName} ${user.familyName}`
      : user?.givenName ?? user?.familyName ?? null;
  const emailLocalPart = user?.email?.split('@')[0] ?? null;
  const displayName = fullName ?? emailLocalPart ?? 'User';

  // WHY: Avatar prefers initials from given/family name (e.g., "BR") over
  // the first two characters of the email local-part (e.g., "BR" for
  // "browe@..."). Falls back to first two letters of the display name.
  const avatarInitials = (() => {
    if (user?.givenName && user.familyName) {
      return `${user.givenName.charAt(0)}${user.familyName.charAt(0)}`.toUpperCase();
    }
    if (user?.givenName) return user.givenName.slice(0, 2).toUpperCase();
    return displayName.slice(0, 2).toUpperCase();
  })();

  const roleTitle = userRoles.includes('owner')
    ? 'Owner'
    : userRoles.includes('coordinator')
      ? 'Coordinator'
      : userRoles.includes('executive')
        ? 'Executive'
        : userRoles.includes('operations')
          ? 'Operations'
          : 'User';

  return (
    <MantineAppShell
      navbar={{ width: 220, breakpoint: 'sm' }}
      padding={0}
    >
      <MantineAppShell.Navbar className={styles['navbar']}>
        {/* Logo / Brand */}
        <div className={styles['brand']}>
          <div className={styles['brandName']}>Synapse<span className={styles['brandDot']}>.</span></div>
          <div className={styles['brandSubtitle']}>IT & Security Reporting</div>
        </div>

        {/* Navigation sections */}
        <nav className={styles['nav']} aria-label="Main navigation">
          {sections.map((section) => (
            <div key={section.section} className={styles['section']}>
              <div className={styles['sectionLabel']}>{section.label}</div>
              {section.links.map((link) => (
                <button
                  key={link.path}
                  className={`${styles['navLink']} ${isActive(link.path) ? styles['navLinkActive'] : ''}`}
                  onClick={() => navigate(link.path)}
                  aria-current={isActive(link.path) ? 'page' : undefined}
                  type="button"
                >
                  <span className={styles['navIcon']}>{link.icon}</span>
                  <span className={styles['navLabel']}>{link.label}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>

        {/* User info footer */}
        <div className={styles['userFooter']}>
          <div className={styles['userAvatar']}>
            {avatarInitials}
          </div>
          <div className={styles['userInfo']}>
            <div className={styles['userName']}>{displayName}</div>
            <div className={styles['userRole']}>{roleTitle}</div>
          </div>
          <button
            className={styles['signOutBtn']}
            onClick={() => void signOut()}
            aria-label="Sign out"
            type="button"
          >
            ↗
          </button>
        </div>
      </MantineAppShell.Navbar>

      <MantineAppShell.Main className={styles['main']}>
        {children}
      </MantineAppShell.Main>
    </MantineAppShell>
  );
}
